import {
    fetchExtendedWorkspace,
    selectActiveWorkspaceId,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { initializeFires } from "features/entity-caching/fires/thunks"
import { clearFlightsState } from "features/entity-caching/flights/slice"
import { listLicensesForUser } from "features/entity-caching/licenses/thunks"
import { listMembersForWorkspace } from "features/entity-caching/members/UserManagementSlice"
import { selectIsProjectLoading } from "features/entity-caching/project/module"
import { selectActivatedFireId } from "features/map/control/selectors"
import { clearMapControlState, deactivateFire } from "features/map/control/slice"
import { loadAndZoomProject } from "features/map/control/thunks"
import { clearMapLayersState } from "features/map/layers/slice"
import {
    clearPreparingToWatch,
    clearPubsubState,
    selectPreparingToWatch,
    selectWatching,
    subscribeToMap,
    unsubscribeToMap,
} from "features/pubsub/slice"
import { clearPubsubSocketsMapState } from "features/pubsub/sockets/map"
import { clearPubsubSocketsProjectState } from "features/pubsub/sockets/projects"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { applyLoadedPDFTemplate } from "plugins/PDFv2/PDFSlice"
import { loadPDFDefaultTemplate } from "plugins/PDFv2/thunks"
import { VideoControllerFactory } from "plugins/Video/controllers/VideoControllerFactory"
import { userLocationStore } from "features/user-locations/user-location-store"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { getSnapshotState } from "runlevel/actions"
import { selectUserID } from "user/profile/selectors"

const isAccessDenied = (reason: unknown) => {
    const rejection = (reason ?? {}) as { message?: string; error?: { message?: string } }
    const message = (rejection.error?.message ?? rejection.message ?? "").toLowerCase()
    return (
        message.includes("permission denied") ||
        message.startsWith("api error: 401") ||
        message.startsWith("api error: 403")
    )
}

export const useWorkspaceInitializer = () => {
    const [readyId, setReadyId] = useState("")
    const dispatch = useAppDispatch()
    const { workspaceId } = useParams()
    const userId = useAppSelector(selectUserID)
    const [unauthorized, setUnauthorized] = useState(false)
    useEffect(() => {
        if (!workspaceId) {
            return setReadyId("")
        }
        if (readyId === workspaceId) return
        const activate = async () => {
            const [template, __, workspace] = await Promise.allSettled([
                dispatch(loadPDFDefaultTemplate({ workspaceId })).unwrap(),
                dispatch(listMembersForWorkspace({ workspaceId })),
                dispatch(fetchExtendedWorkspace(workspaceId)).unwrap(),
                dispatch(listLicensesForUser({ ownerId: userId ?? "" })),
            ])
            if (workspace.status === "rejected" && isAccessDenied(workspace.reason)) {
                setReadyId(workspaceId)
                setUnauthorized(true)
                return
            }
            if (
                template.status === "fulfilled" &&
                template.value &&
                workspace.status === "fulfilled"
            ) {
                dispatch(
                    applyLoadedPDFTemplate({
                        template: template.value,
                        workspaceId,
                        region: workspace.value.organization.region,
                    })
                )
            }
            setUnauthorized(false)
            dispatch(initializeFires({ organizationId: workspaceId }))
            setReadyId(workspaceId)
        }
        activate()
    }, [dispatch, readyId, userId, workspaceId])
    return { unauthorized, ready: readyId === workspaceId }
}

export const useProjectInitializer = () => {
    const [didRunEffect, setDidRunEffect] = useState(false)
    const dispatch = useAppDispatch()
    const { unauthorized: workspaceUnauthorized, ready: workspaceReady } = useWorkspaceInitializer()
    const [projectUnauthorized, setProjectUnauthorized] = useState(false)
    const { projectId } = useParams()
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const activeProjectId = useAppSelector(selectActivatedFireId)
    const snapShotState = getSnapshotState()
    const navigate = useNavigate()
    useEffect(() => {
        if (!orgId || snapShotState || workspaceUnauthorized || !workspaceReady) {
            return
        }
        if (!projectId) {
            throw new Error("Using project initializer without a projectId param")
        }

        if (projectId === activeProjectId)
            return () => {
                dispatch(deactivateFire())
                dispatch(clearFlightsState())
                dispatch(clearMapControlState())
                dispatch(clearMapLayersState())
                dispatch(clearPubsubSocketsMapState())
                dispatch(clearPubsubSocketsProjectState())
                dispatch(clearPubsubState())
                void VideoControllerFactory.unsafeFlush()
                userLocationStore.flush()
            }

        const tryActivate = async () => {
            try {
                await dispatch(loadAndZoomProject({ projectId, orgId })).unwrap()
                setProjectUnauthorized(false)
            } catch {
                setProjectUnauthorized(true)
            } finally {
                setDidRunEffect(true)
            }
        }
        void tryActivate()
    }, [
        activeProjectId,
        dispatch,
        navigate,
        orgId,
        projectId,
        snapShotState,
        workspaceReady,
        workspaceUnauthorized,
    ])
    const projectIsLoading = useAppSelector(selectIsProjectLoading)
    const watching = useAppSelector(selectWatching())
    const preparingToWatch = useAppSelector(selectPreparingToWatch())
    useEffect(() => {
        if (workspaceUnauthorized || !workspaceReady) return
        if (!activeProjectId || projectIsLoading) return
        if (preparingToWatch) {
            if (watching) {
                dispatch(unsubscribeToMap())
                return
            }
            const viewer = preparingToWatch
            dispatch(clearPreparingToWatch())
            dispatch(subscribeToMap(viewer))
        }
    }, [
        activeProjectId,
        dispatch,
        preparingToWatch,
        projectIsLoading,
        watching,
        workspaceReady,
        workspaceUnauthorized,
    ])
    return { unauthorized: workspaceUnauthorized || projectUnauthorized, ready: didRunEffect }
}
